import type { Icon } from '@phosphor-icons/react'
import { Briefcase, CalendarCheck, CheckCircle, Envelope, FileText, House, SealCheck, ShieldCheck, Stack, User } from '@phosphor-icons/react'
import { workCount } from '@/content/work'
import { BOOKING_URL, site } from '@/content/site'

/**
 * The route registry: the one list that drives the router, the sidebar, the search palette
 * and every page title. Add a page = add one entry here.
 */

export type NavGroupId = 'menu' | 'discover' | 'general'

export const NAV_GROUPS: { id: NavGroupId; label: string }[] = [
  { id: 'menu', label: 'Menu' },
  { id: 'discover', label: 'Discover' },
  { id: 'general', label: 'General' },
]

export type PageRoute = {
  kind: 'page'
  id: string
  path: string
  /** Short name for the sidebar and search. */
  label: string
  /** Unique document title. */
  title: string
  description: string
  icon: Icon
  /** Omit to keep the page out of the sidebar (legal pages). */
  group?: NavGroupId
  badge?: number
  /** A Home section id: the sidebar and search scroll Home to it instead of opening `path`. */
  section?: string
  /** Still the RouteStage placeholder: kept out of the sitemap (RouteStage also marks it noindex).
   *  Remove when the page is built and registered in router.tsx PAGE_COMPONENTS. */
  draft?: true
  /** Reachable but never indexed (marked noindex, kept out of the sitemap and llms.txt). */
  noindex?: true
}

export type ExternalRoute = {
  kind: 'external'
  id: string
  href: string
  label: string
  description: string
  icon: Icon
  group: NavGroupId
}

export type NavEntry = PageRoute | ExternalRoute

/** Every page title ends with your name (content/site.ts). */
const SUFFIX = site.name

export const pages: PageRoute[] = [
  {
    kind: 'page',
    id: 'home',
    path: '/',
    label: 'Home',
    title: `${SUFFIX} | GoHighLevel Virtual Assistant & Automation Expert`,
    description: 'GoHighLevel virtual assistant building CRM automation, workflows, funnels, and Voice AI. 10+ years experience, 50+ automations delivered.',
    icon: House,
    group: 'menu',
  },
  {
    kind: 'page',
    id: 'work',
    draft: true,
    path: '/work',
    label: 'Work',
    title: `Funnels and Websites Built in GoHighLevel | ${SUFFIX}`,
    description: 'Live funnel pages, websites and automation samples built in HighLevel: lead capture, nurture sequences, booking flows and AI-assisted follow-up.',
    icon: Briefcase,
    group: 'menu',
    badge: workCount,
    section: 'work',
  },
  {
    kind: 'page',
    id: 'services',
    draft: true,
    path: '/services',
    label: 'Services',
    title: `GoHighLevel Services: CRM, Automation, Voice AI | ${SUFFIX}`,
    description: 'HighLevel CRM setup, workflow automation, Voice AI agents, email and SMS campaigns, and ongoing support. Audit, build and run tiers for coaches, agencies and e-commerce brands.',
    icon: Stack,
    group: 'menu',
    section: 'services',
  },
  {
    kind: 'page',
    id: 'about',
    draft: true,
    path: '/about',
    label: 'About',
    title: `About ${SUFFIX} | HighLevel Automation Specialist, Philippines`,
    description: 'Carlo Cueto is a GoHighLevel virtual assistant based in the Philippines (GMT+8) with 10+ years of experience, working with clients in Australia and the US.',
    icon: User,
    group: 'menu',
    section: 'about',
  },
  {
    kind: 'page',
    id: 'proof',
    path: '/proof',
    label: 'Testimonials',
    title: `Client Testimonials | ${SUFFIX}, GoHighLevel VA`,
    description: 'A video testimonial and the HighLevel accounts Carlo Cueto sets up, automates and supports for coaches, agencies and e-commerce brands.',
    icon: SealCheck,
    group: 'discover',
    section: 'proof',
  },
  {
    kind: 'page',
    id: 'contact',
    path: '/contact',
    label: 'Contact',
    title: `Contact ${SUFFIX} | Book a GoHighLevel Call`,
    description: 'Book a call or send a message about your HighLevel setup. Replies by email, calendar booking available for urgent work.',
    icon: Envelope,
    group: 'general',
    section: 'contact',
  },
  {
    kind: 'page',
    id: 'privacy',
    path: '/privacy',
    label: 'Privacy Policy',
    title: `Privacy Policy | ${SUFFIX}`,
    description: 'How this site handles the information you send through the contact form.',
    icon: ShieldCheck,
  },
  {
    kind: 'page',
    id: 'terms',
    path: '/terms',
    label: 'Terms of Service',
    title: `Terms of Service | ${SUFFIX}`,
    description: 'Terms for using this site and engaging Carlo Cueto for HighLevel work.',
    icon: FileText,
  },
  {
    kind: 'page',
    id: 'thank-you',
    noindex: true,
    path: '/thank-you',
    label: 'Thank you',
    title: `Thanks, your message is in | ${SUFFIX}`,
    description: 'Your message has been received.',
    icon: CheckCircle,
  },
]

export const externals: ExternalRoute[] = [
  {
    kind: 'external',
    id: 'book',
    href: BOOKING_URL,
    label: 'Book a call',
    description: 'Pick a time on my Google Calendar for a call about your HighLevel setup.',
    icon: CalendarCheck,
    group: 'general',
  },
]

/** Sidebar entries per group, in registry order (pages first, then external links). */
export function navEntries(group: NavGroupId): NavEntry[] {
  return [...pages.filter((p) => p.group === group), ...externals.filter((e) => e.group === group)]
}

/** Where a nav entry goes: its Home section when it has one, else its page. */
export function navTarget(page: PageRoute): string {
  return page.section ? `/#${page.section}` : page.path
}

export const notFoundTitle = `Page Not Found | ${SUFFIX}`
